import React from 'react';
import {
  IntervalType,
  InvoiceStatus,
  InvoiceType,
  type Caregiver,
  type FamilyInvoiceSchedule,
  type Invoice,
  type InvoiceStatistics,
  type Patient,
} from '../../../types';
import type { DemoRoute } from '../demoMode';
import type { FeatureTour } from '../tourTypes';

const InvoicesPage = React.lazy(() => import('../../invoices/InvoicesPage'));

// Invented demo data only — no real person or agency ("Demo Pflege GmbH").
const daysAgo = (d: number) => new Date(Date.now() - d * 86400_000).toISOString();
const dateOnly = (d: number) => daysAgo(d).slice(0, 10);

const person = (id: string, firstName: string, lastName: string, extra: Record<string, unknown> = {}) =>
  ({ id, agencyId: 'demo', firstName, lastName, ...extra });

const PATIENTS = [
  person('p1', 'Josefine', 'Huber', { city: 'Graz', email: 'familie.huber@example.at' }),
  person('p2', 'Franz', 'Leitner', { city: 'Leoben', email: 'leitner@example.at' }),
  person('p3', 'Hermine', 'Gruber', { city: 'Wien', email: 'gruber.h@example.at' }),
  person('p4', 'Karl', 'Wagner', { city: 'Linz', email: 'k.wagner@example.at' }),
] as unknown as Patient[];

const CAREGIVERS = [
  person('c1', 'Ana', 'Popescu'),
  person('c2', 'Mária', 'Kovács'),
  person('c3', 'Ewa', 'Nowak'),
] as unknown as Caregiver[];

const P = (id: string) => PATIENTS.find((p) => p.id === id)!;
const C = (id: string) => CAREGIVERS.find((c) => c.id === id)!;

interface Row {
  id: string;
  no: string;
  status: InvoiceStatus;
  patient: string;
  caregiver?: string;
  days: number;
  rate: number;
  transport?: number;
  /** Rechnungsdatum, Tage zurück */
  ago: number;
  vat: boolean;
  mahnung?: number;
}

const ROWS: Row[] = [
  { id: 'i1', no: 'RE-2026-0148', status: InvoiceStatus.DRAFT, patient: 'p1', caregiver: 'c1', days: 28, rate: 60, transport: 90, ago: 0, vat: false },
  { id: 'i2', no: 'RE-2026-0147', status: InvoiceStatus.SENT, patient: 'p2', days: 1, rate: 290, ago: 2, vat: true },
  { id: 'i3', no: 'RE-2026-0146', status: InvoiceStatus.SENT, patient: 'p3', caregiver: 'c2', days: 14, rate: 62, transport: 80, ago: 5, vat: false },
  { id: 'i4', no: 'RE-2026-0145', status: InvoiceStatus.PAID, patient: 'p4', caregiver: 'c3', days: 28, rate: 58, transport: 90, ago: 9, vat: false },
  { id: 'i5', no: 'RE-2026-0144', status: InvoiceStatus.OVERDUE, patient: 'p1', days: 1, rate: 290, ago: 34, vat: true, mahnung: 1 },
  { id: 'i6', no: 'RE-2026-0143', status: InvoiceStatus.PAID, patient: 'p3', days: 1, rate: 290, ago: 36, vat: true },
  { id: 'i7', no: 'RE-2026-0142', status: InvoiceStatus.PAID, patient: 'p2', caregiver: 'c1', days: 28, rate: 60, transport: 90, ago: 40, vat: false },
  { id: 'i8', no: 'RE-2026-0141', status: InvoiceStatus.FREIGEGEBEN, patient: 'p4', caregiver: 'c3', days: 14, rate: 58, ago: 43, vat: false },
];

const round = (n: number) => Math.round(n * 100) / 100;

const toInvoice = (r: Row): Invoice => {
  const care = r.caregiver
    ? `Betreuung ${r.days} Tage à ${r.rate.toFixed(2).replace('.', ',')} €`
    : 'Agenturpauschale Monat';
  const items = [
    { id: `${r.id}-1`, description: care, unitPrice: r.rate, quantity: r.days, taxRate: r.vat ? 20 : 0 },
    ...(r.transport ? [{ id: `${r.id}-2`, description: 'Transportkosten', unitPrice: r.transport, quantity: 1, taxRate: 0 }] : []),
  ];
  const net = round(items.reduce((s, i) => s + i.unitPrice * i.quantity, 0));
  const tax = round(items.reduce((s, i) => s + (i.unitPrice * i.quantity * (i.taxRate || 0)) / 100, 0));
  const start = r.ago + r.days;
  const sent = r.status !== InvoiceStatus.DRAFT && r.status !== InvoiceStatus.FREIGEGEBEN;
  return {
    id: r.id,
    invoiceNumber: r.no,
    type: r.caregiver ? InvoiceType.AUTOMATIC : InvoiceType.FAMILY,
    status: r.status,
    patientId: r.patient,
    caregiverId: r.caregiver,
    description: r.caregiver ? `Turnus ${dateOnly(start)} – ${dateOnly(r.ago)}` : 'Agenturleistung',
    startDate: dateOnly(start),
    endDate: dateOnly(r.ago),
    totalDays: r.days,
    dailyRate: r.rate,
    totalAmount: round(net + tax),
    displayTotals: { net, tax, gross: round(net + tax) },
    invoiceDate: dateOnly(r.ago),
    dueDate: dateOnly(r.ago - 14),
    daysUntilDue: 14 - r.ago,
    sentAt: sent ? daysAgo(r.ago) : undefined,
    paidAt: r.status === InvoiceStatus.PAID ? daysAgo(Math.max(0, r.ago - 7)) : undefined,
    invoiceRecipient: 'patient',
    creationMode: r.caregiver ? 'caregiver-to-client' : 'agency-to-client',
    mahnungCount: r.mahnung || 0,
    lastMahnungSentAt: r.mahnung ? daysAgo(3) : undefined,
    createdAt: daysAgo(r.ago),
    updatedAt: daysAgo(r.ago),
    patient: P(r.patient),
    caregiver: r.caregiver ? C(r.caregiver) : undefined,
    items,
    assignedStaffName: 'Sabine Koller',
  };
};

const INVOICES = ROWS.map(toInvoice);

const STATISTICS: InvoiceStatistics = (() => {
  const by = (s: InvoiceStatus) => INVOICES.filter((i) => i.status === s);
  const sum = (list: Invoice[]) => round(list.reduce((s, i) => s + i.totalAmount, 0));
  const paid = by(InvoiceStatus.PAID);
  return {
    totalInvoices: INVOICES.length,
    draftInvoices: by(InvoiceStatus.DRAFT).length,
    sentInvoices: by(InvoiceStatus.SENT).length,
    paidInvoices: paid.length,
    overdueInvoices: by(InvoiceStatus.OVERDUE).length,
    totalAmount: sum(INVOICES),
    paidAmount: sum(paid),
    pendingAmount: round(sum(INVOICES) - sum(paid)),
  };
})();

const page = <T,>(data: T[], params: Record<string, any>) => {
  const p = Number(params.page) || 1;
  const limit = Number(params.limit) || 10;
  return { data: data.slice((p - 1) * limit, p * limit), total: data.length, page: p, limit, totalPages: Math.max(1, Math.ceil(data.length / limit)) };
};

const listInvoices = (params: Record<string, any>) => {
  let list = INVOICES;
  if (params.status) list = list.filter((i) => i.status === params.status);
  if (params.type) list = list.filter((i) => i.type === params.type);
  if (params.hasMahnung) list = list.filter((i) => (i.mahnungCount || 0) > 0);
  if (params.search) {
    const q = String(params.search).toLowerCase();
    list = list.filter((i) =>
      [i.invoiceNumber, i.patient?.firstName, i.patient?.lastName, i.caregiver?.firstName, i.caregiver?.lastName]
        .some((v) => (v || '').toLowerCase().includes(q)));
  }
  return page(list, params);
};

const schedules = (params: Record<string, any>) => {
  // The page keeps only schedules of the user's own agency, so echo it back.
  const agencyId = params.agencyId || 'demo';
  const schedule: FamilyInvoiceSchedule = {
    id: 's1', patientId: 'p3', agencyId, startDate: dateOnly(60), intervalType: IntervalType.MONTHS, intervalValue: 1,
    intervalDays: 30, dailyRate: 0, lastInvoiceDate: dateOnly(36), nextInvoiceDate: dateOnly(-24), isActive: true,
    creationMode: 'agency-to-client', invoiceRecipient: 'patient', createdAt: daysAgo(60), updatedAt: daysAgo(36),
    patient: P('p3'),
    amounts: [{ id: 'a1', familyScheduleId: 's1', amount: 290, description: 'Agenturpauschale Monat', taxRate: 20, createdAt: daysAgo(60), updatedAt: daysAgo(60) }],
  };
  return page([schedule], params);
};

// --- A small, valid PDF built in the browser (no library, latin-1 text) ---
const money = (n: number) => `${n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')} EUR`;
const de = (iso?: string) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '');
const esc = (s: string) => s.replace(/[\\()]/g, (c) => `\\${c}`).replace(/–/g, '-');

const buildPdf = (inv: Invoice): Blob => {
  const ops: string[] = [];
  const text = (x: number, y: number, size: number, s: string, bold = false) =>
    ops.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${x} ${y} Td (${esc(s)}) Tj ET`);
  const right = (xr: number, y: number, size: number, s: string, bold = false) =>
    text(xr - s.length * size * 0.5, y, size, s, bold);

  ops.push('0.75 0.1 0.25 rg 0 800 595 42 re f 1 1 1 rg');
  text(50, 815, 16, 'Demo Pflege GmbH', true);
  ops.push('0 0 0 rg');
  text(50, 780, 9, 'Demo Pflege GmbH - Musterstraße 1 - 8010 Graz');
  const pt = inv.patient;
  text(50, 750, 11, `${pt?.firstName ?? ''} ${pt?.lastName ?? ''}`);
  text(50, 736, 11, 'Beispielgasse 12');
  text(50, 722, 11, `8010 ${(pt as any)?.city ?? 'Graz'}`);
  text(380, 750, 10, `Rechnungsnummer: ${inv.invoiceNumber}`);
  text(380, 736, 10, `Rechnungsdatum: ${de(inv.invoiceDate)}`);
  text(380, 722, 10, `Fällig am: ${de(inv.dueDate)}`);
  text(50, 680, 18, 'Rechnung', true);
  if (inv.caregiver) text(50, 662, 10, `Betreuung durch ${inv.caregiver.firstName} ${inv.caregiver.lastName}, ${de(inv.startDate)} - ${de(inv.endDate)}`);

  let y = 630;
  ops.push('0.95 0.95 0.95 rg 50 624 495 20 re f 0 0 0 rg');
  text(56, y, 10, 'Beschreibung', true);
  right(400, y, 10, 'Menge', true);
  right(540, y, 10, 'Betrag', true);
  (inv.items || []).forEach((it) => {
    y -= 24;
    text(56, y, 10, it.description);
    right(400, y, 10, String(it.quantity));
    right(540, y, 10, money(it.unitPrice * it.quantity));
  });
  const t = inv.displayTotals!;
  y -= 16;
  ops.push(`0.6 0.6 0.6 RG 300 ${y} m 545 ${y} l S`);
  y -= 18; text(310, y, 10, 'Netto'); right(540, y, 10, money(t.net));
  y -= 16; text(310, y, 10, 'USt.'); right(540, y, 10, money(t.tax));
  y -= 20; text(310, y, 12, 'Gesamtbetrag', true); right(540, y, 12, money(t.gross), true);

  // Payment code area: a decorative pattern standing in for the real QR code.
  const qx = 50; const qy = 170; const cell = 4; const n = 25;
  let seed = inv.invoiceNumber.split('').reduce((s, c) => s + c.charCodeAt(0), 7);
  const finder = (r: number, c: number) => {
    const inBox = (r0: number, c0: number) => r >= r0 && r < r0 + 7 && c >= c0 && c < c0 + 7;
    const ring = (r0: number, c0: number) => {
      const dr = Math.max(Math.abs(r - (r0 + 3)), Math.abs(c - (c0 + 3)));
      return dr === 3 || dr <= 1;
    };
    if (inBox(0, 0)) return ring(0, 0) ? 1 : 0;
    if (inBox(0, n - 7)) return ring(0, n - 7) ? 1 : 0;
    if (inBox(n - 7, 0)) return ring(n - 7, 0) ? 1 : 0;
    return -1;
  };
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      seed = (seed * 9301 + 49297) % 233280;
      const f = finder(r, c);
      if (f === 1 || (f === -1 && seed / 233280 > 0.5)) ops.push(`${qx + c * cell} ${qy + (n - 1 - r) * cell} ${cell} ${cell} re f`);
    }
  }
  text(170, 250, 11, 'Bequem bezahlen', true);
  text(170, 234, 10, 'Code mit der Banking-App scannen - Betrag, IBAN und');
  text(170, 220, 10, `Verwendungszweck ${inv.invoiceNumber} sind bereits ausgefüllt.`);
  text(170, 196, 10, 'IBAN: AT00 0000 0000 0000 0000 (Beispiel)');
  text(50, 80, 8, 'Demo Pflege GmbH - Beispieldaten für die Vorschau');

  const content = ops.join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> >> /Contents 6 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((o, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${o}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  pdf += offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('');
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  // One byte per character, so umlauts land as WinAnsi (latin-1) bytes.
  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i += 1) bytes[i] = pdf.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: 'application/pdf' });
};

const byId = (id: string) => INVOICES.find((i) => i.id === id) || INVOICES[0];

const mocks: DemoRoute[] = [
  { path: /^\/invoices$/, reply: ({ params }) => listInvoices(params) },
  { path: /^\/invoices\/statistics$/, reply: () => STATISTICS },
  { path: /^\/invoices\/due-schedules$/, reply: () => [] },
  { path: /^\/invoices\/([^/]+)\/download$/, delayMs: 600, reply: ({ match }) => buildPdf(byId(match[1])) },
  { path: /^\/invoices\/([^/]+)$/, reply: ({ match }) => byId(match[1]) },
  { path: /^\/family-invoice-schedules$/, reply: ({ params }) => schedules(params) },
  { path: /^\/patients$/, reply: ({ params }) => page(PATIENTS, { ...params, limit: 1000 }) },
  { path: /^\/caregivers$/, reply: ({ params }) => page(CAREGIVERS, { ...params, limit: 1000 }) },
  // "Vorschau" on a recurring schedule: the demo id 's1' would 404 on the server.
  { path: /^\/invoices\/schedule-preview\/[^/]+\/pdf$/, delayMs: 600, reply: () => buildPdf(INVOICES.find((i) => i.patient?.id === 'p3') || INVOICES[0]) },
  // Writes whose response the page reads — the generic `data: null` would
  // show empty/"undefined" toasts or throw.
  { method: 'post', path: /^\/invoices\/bulk-(freigegeben|abgeschlossen|paid)$/, reply: ({ body }) => ({ success: true, message: `${body?.ids?.length || 0} Rechnung(en) aktualisiert`, updatedCount: body?.ids?.length || 0 }) },
  { method: 'post', path: /^\/invoices\/process-recurring$/, delayMs: 800, reply: () => ({ processed: 0, successful: 0, failed: 0, errors: [] }) },
  // ZIP download expects a Blob (createObjectURL) — an empty, valid ZIP.
  {
    method: 'post',
    path: /^\/invoices\/bulk-download$/,
    delayMs: 600,
    reply: () => new Blob([new Uint8Array([0x50, 0x4b, 0x05, 0x06, ...new Array(18).fill(0)])], { type: 'application/zip' }),
  },
  {
    method: 'post',
    path: /^\/invoices\/([^/]+)\/correction$/,
    reply: ({ match, body }) => {
      const orig = byId(match[1]);
      return { ...orig, id: `${orig.id}-k`, invoiceNumber: `${orig.invoiceNumber}-${body?.kind === 'storno' ? 'ST' : 'GS'}`, status: InvoiceStatus.DRAFT };
    },
  },
];

const k = (key: string) => `featurePreview.invoices.${key}`;

export const invoicesTour: FeatureTour = {
  Page: InvoicesPage,
  mocks,
  chapters: [
    {
      title: k('tourOverviewTitle'),
      steps: [
        { at: 0.5, target: 'inv-stats', balloon: { title: k('tourStatsTitle'), text: k('tourStatsText') } },
        { at: 4.5, target: 'inv-list', balloon: { title: k('hs3Title'), text: k('hs3Text') } },
        { at: 9, target: 'inv-new', balloon: { title: k('hs1Title'), text: k('hs1Text') } },
      ],
      duration: 13,
    },
    {
      title: k('ch2'),
      steps: [
        { at: 0.5, target: 'inv-open', balloon: { title: k('tourOpenTitle'), text: k('tourOpenText') } },
        { at: 2.5, target: 'inv-open', action: 'click' },
        { at: 4, target: 'inv-pdf', balloon: { title: k('hs2Title'), text: k('hs2Text') } },
        { at: 9, target: 'inv-pdf', balloon: { title: k('tourPdfTitle'), text: k('tourPdfText') } },
      ],
      duration: 13,
    },
    {
      title: k('ch3'),
      steps: [
        { at: 0.5, target: 'inv-actions', balloon: { title: k('tourActionsTitle'), text: k('tourActionsText') } },
        { at: 2.5, target: 'inv-actions', action: 'click' },
        { at: 3.5, target: 'inv-send', balloon: { title: k('tourSendTitle'), text: k('tourSendText') } },
        { at: 8.5, target: 'inv-send', balloon: { title: k('tourRemindTitle'), text: k('tourRemindText') } },
      ],
      duration: 13,
    },
  ],
  savings: {
    aLabel: k('savA'), aDefault: 40, aMax: 300,
    bLabel: k('savB'), b: 10,
    toHoursPerMonth: (a, b) => (a * b) / 60,
  },
};
